// Replies that are queued, sending, on hold or failed, shown at the end of the
// thread as outbound-styled bubbles.
//
// This is what "Send returns at once" looks like: the composer has already
// cleared, and the message is right here, in the conversation, with a status.
// It is driven by the app-wide outbox (lib/send-outbox.ts), so it is also the
// place a reply that failed while you were elsewhere is waiting for you.

import { Check, Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { MessageBody } from '@/components/MessageBody';
import { UserAvatar } from '@/components/UserAvatar';
import { Button } from '@/components/ui/button';
import { htmlToPlain } from '@/lib/html-normalize';
import { humanizeError } from '@/lib/toast';
import type { ReplyOutboxPayload } from '@/lib/outbound-send';
import { discardJob, retryJob } from '@/lib/outbox-jobs';
import { requestRestore } from '@/lib/outbox-restore';
import { outbox, undoSecondsLeft, type OutboxJobView } from '@/lib/send-outbox';
import { useAuth } from '@/lib/auth-guard';
import { useNow, useOutboxJobs } from '@/lib/use-send-outbox';
import { cn } from '@/lib/utils';
import type { TeamMember } from '@/lib/queries';

interface Props {
  ticketId: string;
  teamMembersBySlackId: Map<string, TeamMember>;
}

export function PendingReplies({ ticketId, teamMembersBySlackId }: Props) {
  const jobs = useOutboxJobs(ticketId).filter((j) => j.kind === 'reply');
  // Tick only while something is counting down.
  const now = useNow(jobs.some((j) => j.state === 'queued'));
  const { teamMember } = useAuth();

  if (jobs.length === 0) return null;

  return (
    <div className="flex flex-col gap-3" data-testid="pending-replies">
      {jobs.map((job) => (
        <PendingReply
          key={job.id}
          job={job as OutboxJobView<ReplyOutboxPayload>}
          now={now}
          name={teamMember?.name ?? 'You'}
          slackId={teamMember?.slack_user_id ?? null}
          teamMembersBySlackId={teamMembersBySlackId}
        />
      ))}
    </div>
  );
}

function PendingReply({
  job,
  now,
  name,
  slackId,
  teamMembersBySlackId,
}: {
  job: OutboxJobView<ReplyOutboxPayload>;
  now: number;
  name: string;
  slackId: string | null;
  teamMembersBySlackId: Map<string, TeamMember>;
}) {
  const p = job.payload;
  const unsent = job.state === 'failed' || job.state === 'held';
  const files = p.snapshot.files;

  return (
    <article
      data-pending-reply={job.id}
      data-state={job.state}
      className={cn(
        // Same shell as a delivered outbound message (ticket-detail.tsx), so the
        // bubble simply "settles" into the thread when the real one arrives.
        'min-w-0 overflow-hidden rounded-lg border border-l-4 bg-background',
        unsent
          ? 'border-tint-critical-ring/60 border-l-red-500 bg-tint-critical/20'
          : 'border-tint-success-ring/60 border-l-emerald-500 bg-tint-success/30',
        'dark:border-slate-300 dark:bg-slate-100 dark:text-slate-900',
        unsent ? 'dark:border-l-red-600' : 'dark:border-l-emerald-600',
        // Not delivered yet: a little quieter than the real messages.
        job.state === 'queued' || job.state === 'sending' ? 'opacity-90' : null,
      )}
    >
      <header className="flex items-start gap-3 px-3.5 pt-3">
        {slackId ? (
          <UserAvatar slackId={slackId} teamMembersBySlackId={teamMembersBySlackId} size="md" />
        ) : (
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-tint-success text-2xs font-medium text-tint-success-fg dark:bg-emerald-100 dark:text-emerald-800">
            {name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
            <p className="min-w-0 truncate text-sm font-semibold text-foreground dark:text-slate-900">
              {name}
            </p>
            <StatusLabel job={job} now={now} />
          </div>
          <p className="max-w-full break-all text-2xs text-muted-foreground dark:text-slate-500">
            {p.to.length > 0 ? <span>to {p.to.join(', ')}</span> : null}
            {p.cc.length > 0 ? <span> · cc {p.cc.join(', ')}</span> : null}
          </p>
        </div>
      </header>

      <div className="px-3.5 py-3">
        <MessageBody
          body={htmlToPlain(p.snapshot.bodyHtml) || '(no text)'}
          bodyHtml={p.snapshot.bodyHtml}
          attachments={null}
          variant="outbound"
          allowBlobImages
        />
        {job.state === 'failed' && job.error ? (
          <p className="mt-2 break-words text-2xs text-tint-critical-fg dark:text-red-700">
            {humanizeError(job.error)}
          </p>
        ) : null}
        {files.length > 0 ? (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {files.map((f) => (
              <li
                key={f.localId}
                className="inline-flex max-w-full items-center gap-1 rounded border border-border/60 bg-background/70 px-1.5 py-0.5 text-2xs text-muted-foreground dark:border-slate-300 dark:bg-white dark:text-slate-600"
              >
                <Paperclip className="h-3 w-3 shrink-0" aria-hidden />
                <span className="truncate">{f.file.name}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <Actions job={job} />
    </article>
  );
}

function StatusLabel({ job, now }: { job: OutboxJobView<ReplyOutboxPayload>; now: number }) {
  const base = 'shrink-0 text-2xs';
  switch (job.state) {
    case 'queued':
      return (
        <span className={cn(base, 'text-muted-foreground dark:text-slate-500')}>
          Sending in {Math.max(1, undoSecondsLeft(job, now))}s…
        </span>
      );
    case 'sending':
      return (
        <span
          className={cn(base, 'inline-flex items-center gap-1 text-muted-foreground dark:text-slate-500')}
        >
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          {job.stage ?? 'Sending…'}
        </span>
      );
    case 'sent':
      return (
        <span
          className={cn(base, 'inline-flex items-center gap-1 text-tint-success-fg dark:text-emerald-700')}
        >
          <Check className="h-3 w-3" aria-hidden />
          Sent
        </span>
      );
    case 'failed':
      return (
        <span className={cn(base, 'font-medium text-tint-critical-fg dark:text-red-700')}>
          Not sent
        </span>
      );
    case 'held':
      return (
        <span className={cn(base, 'font-medium text-tint-warning-fg dark:text-amber-800')}>
          Not sent — on hold
        </span>
      );
  }
}

function Actions({ job }: { job: OutboxJobView<ReplyOutboxPayload> }) {
  const btn = 'h-6 px-2 text-2xs';

  const edit = () => {
    if (!requestRestore(job)) {
      toast.error("Can't edit this reply here", {
        description: 'The reply box for this ticket is not available.',
      });
    }
  };
  const discard = () => {
    if (window.confirm('Discard this reply? It has not been sent.')) discardJob(job.id);
  };

  if (job.state === 'queued') {
    return (
      <div className="flex flex-wrap gap-1.5 border-t border-border/40 px-3 py-1.5 dark:border-slate-300">
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={() => outbox.undo(job.id)}>
          Undo
        </Button>
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={() => outbox.sendNow(job.id)}>
          Send now
        </Button>
      </div>
    );
  }
  if (job.state === 'failed') {
    return (
      <div className="flex flex-wrap gap-1.5 border-t border-border/40 px-3 py-1.5 dark:border-slate-300">
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={() => retryJob(job.id)}>
          Retry
        </Button>
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={edit}>
          Edit
        </Button>
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={discard}>
          Discard
        </Button>
      </div>
    );
  }
  if (job.state === 'held') {
    return (
      <div className="flex flex-wrap gap-1.5 border-t border-border/40 px-3 py-1.5 dark:border-slate-300">
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={edit}>
          Edit
        </Button>
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={() => outbox.sendNow(job.id)}>
          Send
        </Button>
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={discard}>
          Discard
        </Button>
      </div>
    );
  }
  return null;
}
